/**
 * #2465 — client-side overlap pre-check that mirrors the server's CallLog
 * validator (apps/projects/apis/serializer.py). The server rejects a CallLog
 * whose drive / call / drive-back times overlap ANY existing segment that day
 * ("Call time overlapping another call time…", "Drive time overlapping…", etc.).
 *
 * The offline-first PWA must catch the conflict BEFORE it queues the write, so a
 * doomed entry never lands in Sync Issues. The legacy app gets the same protection
 * for free because it POSTs synchronously and shows the 400 inline; the PWA needs
 * this pre-check to match it.
 *
 * Source of truth for the timeline = GET /api/projects/calllog/day-timeline/,
 * which already decomposes each CallLog into drive / store_visit / drive_back
 * segments. The OLD manual-entry check only compared against store_visit, so it
 * missed the drive legs — this checks every segment type.
 */
import type { DayTimelineEntry } from '../services/api';

/** Minutes-since-midnight from a local ISO datetime (the day-timeline tz). */
export function isoLocalToMinutes(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.getHours() * 60 + d.getMinutes();
}

/** Human label for a conflicting segment, for the inline warning. */
export function segmentLabel(e: DayTimelineEntry): string {
  if (e.type === 'store_visit') {
    const store = e.store_name || (e.legacy_store_id ? `Store #${e.legacy_store_id}` : 'a store visit');
    return e.project_name ? `${store} — ${e.project_name}` : store;
  }
  return e.type === 'drive' ? 'a drive' : 'a drive back';
}

function fmt(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  const ap = h < 12 ? 'AM' : 'PM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${ap}`;
}

export interface TimelineOverlap {
  conflict: DayTimelineEntry | null;
  message: string | null;
}

/**
 * Check a candidate [startMin, endMin) window against EVERY timeline segment.
 * Half-open comparison (touching edges don't conflict). An in-flight segment
 * (no end_time) extends to end-of-day. Pass ignoreCalllogId when EDITING an
 * existing entry so it doesn't conflict with itself.
 */
export function findTimelineOverlap(
  entries: DayTimelineEntry[],
  startMin: number,
  endMin: number,
  opts: { ignoreCalllogId?: number } = {},
): TimelineOverlap {
  for (const e of entries) {
    if (opts.ignoreCalllogId != null && e.calllog_id === opts.ignoreCalllogId) continue;
    const s = isoLocalToMinutes(e.start_time);
    if (s === null) continue;
    const rawEnd = isoLocalToMinutes(e.end_time);
    const en = rawEnd ?? 24 * 60; // in-flight -> end of day
    if (startMin < en && endMin > s) {
      const shownEnd = rawEnd === null ? 'now' : fmt(en);
      return {
        conflict: e,
        message: `Conflicts with ${segmentLabel(e)} (${fmt(s)}–${shownEnd}).`,
      };
    }
  }
  return { conflict: null, message: null };
}
